'use memo'

import { useState } from 'react'
import { usePluginConfig } from '../hooks/usePluginConfig'
import { usePluginKeys } from '../hooks/usePluginKeys'
import { selectPluginKey } from '../utils/selectPluginKey'
import { PluginBuilderSkeleton } from './PluginBuilderSkeleton'
import { PluginKeysSection } from './PluginKeysSection'
import { PluginSnippetBuilder } from './PluginSnippetBuilder'

interface WebsitePluginProps {
  accountUid: string | undefined
}

/**
 * Account settings › Website Plugin: the account's keys on top, then the
 * snippet builder — settings, a live preview and the embed code.
 */
export const WebsitePlugin = ({ accountUid }: WebsitePluginProps) => {
  const { data: config, isLoading: configLoading } = usePluginConfig(accountUid)
  const {
    data: pluginKeys,
    isLoading: keysLoading,
    isError: keysError,
  } = usePluginKeys(accountUid)
  const [chosenKeyId, setChosenKeyId] = useState<string | null>(null)

  if (!accountUid) {
    return null
  }

  // Only the organization id is read from the config. A failed config load
  // still opens the builder, with the ID left for the admin to enter.
  const organizationId = config?.organization_id ?? null
  const keys = pluginKeys ?? []

  return (
    <>
      <PluginKeysSection accountUid={accountUid} />
      {(configLoading && !config) || (keysLoading && !pluginKeys) ? (
        <PluginBuilderSkeleton />
      ) : (
        // Keyed on the organization, so the form starts from the right ID.
        <PluginSnippetBuilder
          key={organizationId ?? 'no-organization'}
          organizationId={organizationId}
          pluginKey={selectPluginKey(keys, chosenKeyId)}
          pluginKeys={keys}
          onKeyChange={setChosenKeyId}
          // A failed load isn't "no keys": don't ask for a key that may exist.
          keysFailed={keysError && !pluginKeys}
        />
      )}
    </>
  )
}
